import type { Goal, UserProject, UserProjectGrade } from '@teachur/http-client';
import { UserProjectStatus } from '@teachur/http-client';

export const getInitialReviewState = (grades?: UserProjectGrade[]) => {
  return (
    grades?.reduce<{
      ratings: Record<number, number>;
      goalNotes: Record<number, string>;
    }>(
      (acc, grade) => {
        if (grade.goalId == null) return acc;

        acc.ratings[grade.goalId] = grade.grade;
        acc.goalNotes[grade.goalId] = grade.note ?? '';

        return acc;
      },
      {
        ratings: {},
        goalNotes: {},
      }
    ) ?? {
      ratings: {},
      goalNotes: {},
    }
  );
};

export const getProjectReviewPermissions = ({
  selectedProject,
  currentUserId,
  isAdmin = false,
  isCourseCompleted = false,
}: {
  selectedProject: UserProject | null;
  currentUserId?: number;
  isAdmin?: boolean;
  isCourseCompleted?: boolean;
}) => {
  const isFinalStatus =
    selectedProject?.status === UserProjectStatus.Approved ||
    selectedProject?.status === UserProjectStatus.NotYet;

  const isUnclaimed = selectedProject?.instructorId == null;

  const isClaimedByCurrentUser =
    selectedProject?.instructorId != null && selectedProject.instructorId === currentUserId;

  const canAdminOverride = isAdmin && !isCourseCompleted;
  const canReviewProject = canAdminOverride || (!isFinalStatus && isClaimedByCurrentUser);

  return {
    isFinalStatus,
    isUnclaimed,
    isClaimedByCurrentUser,
    canAdminOverride,
    canShowClaimButton: !isAdmin && !isFinalStatus && isUnclaimed,
    canShowDisclaimButton: !isAdmin && isClaimedByCurrentUser && !isFinalStatus,
    canShowAdminUnclaimButton: isAdmin && !isUnclaimed && !isFinalStatus,
    canReviewProject,
    isReviewDisabled: !canReviewProject,
  };
};

export const createProjectReviewPayload = ({
  selectedProject,
  goals,
  ratings,
  goalNotes,
  generalNote,
}: {
  selectedProject: UserProject;
  goals: Goal[];
  ratings: Record<number, number>;
  goalNotes: Record<number, string>;
  generalNote: string;
}) => {
  const now = new Date().toISOString();
  const latestSubmission = getLatestSubmission(selectedProject);

  const isApproved = goals.every((goal) => ratings[goal.id] === 5);

  const userProject: UserProject = {
    ...selectedProject,
    status: isApproved ? UserProjectStatus.Approved : UserProjectStatus.NotYet,
  };

  const grades: UserProjectGrade[] = goals.map((goal) => {
    const existingGrade = latestSubmission?.grades?.find((grade) => grade.goalId === goal.id);

    return {
      id: existingGrade?.id ?? 0,
      grade: ratings[goal.id],
      note: goalNotes[goal.id] ?? '',
      userProjectSubmissionId: latestSubmission?.id ?? 0,
      goalId: goal.id,
      goal: existingGrade?.goal ?? goal,
      createdAt: existingGrade?.createdAt ?? now,
      updatedAt: now,
    };
  });

  return {
    userProject,
    grades,
    note: generalNote,
  };
};

export const getLatestSubmission = (
  userProject: UserProject | null | undefined
): UserProjectSubmission | undefined => {
  if (!userProject?.submissions?.length) return undefined;
  return [...userProject.submissions].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )[0];
};

export const getSortedProjectGoals = (goals?: Goal[]) => {
  return [...(goals ?? [])].sort((a, b) => a.id - b.id);
};
